import { ProgressSpinner } from 'primereact/progressspinner';
import { useCompanyContext } from '../../context/CompanyContext';
import { useRolesAccessContext } from '../../context/RolesAccessContext';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActivePoliciesSummary } from '../../services/Insurance/Policy';
import PolicySummary, {
  PolicySummaryProps,
} from '../../features/insurance/policies/PolicySummary';
import { checkAccess } from '@/src/utils/roleUtils';
import { reportsPerSection } from '@/src/utils/rolesConst';
import ClaimSummary, {
  ClaimSummaryProps,
} from '../../features/insurance/claims/summary/ClaimSummary';
import { fetchClaimsSummary } from '../../services/Insurance/Claim';
import checkPermissions from '../../HOCs/CheckPermissions';

const Dashboard = () => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess, isAdmin } = useRolesAccessContext();

  const { data, isFetching, isError } = useQuery({
    queryKey: ['getPoliciesSummary', selectedCompany],
    queryFn: ({ signal }) =>
      fetchActivePoliciesSummary(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled:
      checkAccess(rolesAcess ?? [], reportsPerSection['policy']) || isAdmin,
  });

  const claimRequest = useQuery({
    queryKey: ['getClaimsSummary', selectedCompany],
    queryFn: ({ signal }) => fetchClaimsSummary(selectedCompany!.id, signal),
    refetchOnWindowFocus: false,
    enabled:
      checkAccess(rolesAcess ?? [], reportsPerSection['claim']) || isAdmin,
  });

  const PolicySummaryProtected = checkPermissions<PolicySummaryProps>({
    sectionsToAccess: reportsPerSection['policy'],
    Children: PolicySummary,
  });

  const ClaimSummaryProtected = checkPermissions<ClaimSummaryProps>({
    sectionsToAccess: reportsPerSection['claim'],
    Children: ClaimSummary,
  });

  if (isFetching || claimRequest.isFetching) {
    return <ProgressSpinner className="mx-auto flex" />;
  }

  return (
    <div className="p-4 pt-3 mx-auto max-w-1400px flex flex-wrap gap-4 xl:row-gap-3 justify-content-center xl:justify-content-start">
      <div className="flex flex-wrap justify-content-center xl:justify-content-start w-full gap-4">
        <div className="w-full max-w-800px">
          <PolicySummaryProtected isError={isError} policies={data ?? []} />
        </div>
        <div className="w-full max-w-700px">
          <ClaimSummaryProtected
            isError={claimRequest.isError}
            policies={claimRequest.data ?? []}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(Dashboard);
